import { useEffect } from "react";
import { motion } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle, Heart, ArrowLeft, Share2 } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const DonationSuccess = () => {
  useEffect(() => {
    toast.success("Thank you for your generous donation!");
  }, []);

  const handleShare = () => {
    const shareText = "I just backed Tachen's reunion documentary! Join me in supporting this incredible story of hope and human connection.";
    const shareUrl = window.location.origin;
    
    if (navigator.share) {
      navigator.share({
        title: "Support Tachen's Documentary",
        text: shareText,
        url: shareUrl,
      });
    } else {
      navigator.clipboard.writeText(`${shareText} ${shareUrl}`);
      toast.success("Link copied to clipboard!");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-muted/20 to-background flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-lg"
      >
        <Card className="border-2 border-story-hope/20 shadow-2xl">
          <CardContent className="p-8 text-center">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
              className="w-20 h-20 bg-story-hope/10 rounded-full flex items-center justify-center mx-auto mb-6"
            >
              <CheckCircle className="w-12 h-12 text-story-hope" />
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-3xl font-serif font-bold text-foreground mb-4"
            >
              Thank You, Miracle Maker!
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="text-lg text-muted-foreground mb-6"
            >
              Your generous donation brings us one step closer to sharing Tachen's incredible story of hope and reunion with the world.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="bg-muted/50 rounded-lg p-4 mb-8"
            >
              <div className="flex items-center justify-center gap-2 text-story-hope mb-2">
                <Heart className="w-5 h-5" />
                <span className="font-semibold">You're now a shareholder!</span>
              </div>
              <p className="text-sm text-muted-foreground">
                You'll receive credit recognition and royalty participation in all documentary proceeds.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="flex flex-col sm:flex-row gap-3"
            >
              <Button
                variant="outline"
                className="flex-1 border-story-peace text-story-peace hover:bg-story-peace hover:text-background"
                onClick={handleShare}
              >
                <Share2 className="w-4 h-4 mr-2" />
                Share the Story
              </Button>
              <Link to="/" className="flex-1">
                <Button className="w-full bg-story-hope hover:bg-story-hope/90 text-black font-semibold">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Home
                </Button>
              </Link>
            </motion.div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default DonationSuccess;
